import { useNavigate } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

const Section = ({ nr, title, children }) => (
  <div>
    <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
      <span className="w-6 h-6 rounded-md bg-[#C9A84C]/15 border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] text-xs font-black shrink-0">{nr}</span>
      {title}
    </h2>
    <div className="text-zinc-400 text-sm leading-relaxed space-y-2 pl-8">{children}</div>
  </div>
);

export default function CookieBeleidPage() {
  const navigate = useNavigate();
  const datum = "1 mei 2026";

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Header */}
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FitSmith" className="h-7 w-7 object-contain" />
            <span className="text-white font-bold tracking-wide">FitSmith</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Titel */}
        <div className="flex items-start gap-4 mb-10">
          <div className="w-12 h-12 rounded-2xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center shrink-0">
            <Cookie size={22} className="text-[#C9A84C]" />
          </div>
          <div>
            <h1 className="text-white font-bold text-3xl mb-1">Cookiebeleid</h1>
            <p className="text-zinc-500 text-sm">Versie: {datum} · FitSmith</p>
          </div>
        </div>

        <div className="space-y-8">
          <Section nr="1" title="Wat zijn cookies?">
            <p>
              Cookies zijn kleine tekstbestanden die op jouw apparaat worden geplaatst wanneer je onze website bezoekt.
              Ze helpen ons om de website goed te laten functioneren en jouw ervaring te verbeteren.
            </p>
          </Section>

          <Section nr="2" title="Welke cookies gebruiken wij?">
            <p className="font-semibold text-zinc-300">Functionele cookies (noodzakelijk)</p>
            <p>
              Deze cookies zijn strikt noodzakelijk voor het correct functioneren van de website. Ze worden gebruikt voor:
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Ingelogd blijven op jouw account (authenticatietoken in <code className="text-[#C9A84C] bg-[#C9A84C]/10 px-1 rounded">localStorage</code>)</li>
              <li>Onthouden van jouw sessie tijdens gebruik van de trainingsschema's</li>
              <li>Kiosk-modus voor trainers (tijdelijke sessie-opslag)</li>
            </ul>
            <p className="mt-2 font-semibold text-zinc-300">Analytische cookies</p>
            <p>
              Wij maken geen gebruik van externe analytische cookies (zoals Google Analytics). Eventuele statistieken
              worden intern bijgehouden zonder gebruik te maken van cookies van derden.
            </p>
            <p className="mt-2 font-semibold text-zinc-300">Marketing- en trackingcookies</p>
            <p>
              Wij plaatsen <strong>geen</strong> marketing- of trackingcookies. Er wordt geen informatie gedeeld
              met advertentienetwerken.
            </p>
          </Section>

          <Section nr="3" title="Cookies van derden">
            <p>
              Onze website maakt gebruik van de volgende diensten van derden, die mogelijk eigen cookies plaatsen:
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>
                <strong className="text-zinc-300">Mollie</strong> — betaaldienst voor het verwerken van betalingen.
                Mollie kan functionele cookies plaatsen tijdens het afrekenproces. Zie het{" "}
                <a href="https://www.mollie.com/nl/privacy" target="_blank" rel="noopener noreferrer"
                  className="text-[#C9A84C] hover:underline">
                  privacybeleid van Mollie
                </a>.
              </li>
            </ul>
          </Section>

          <Section nr="4" title="Hoe lang worden cookies bewaard?">
            <p>
              De bewaartermijn verschilt per cookie:
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Authenticatietoken (ingelogd blijven): maximaal 30 dagen</li>
              <li>Kiosk-sessies: maximaal 5 uur, daarna automatisch verlopen</li>
              <li>Overige sessiecookies: worden verwijderd zodra je de browser sluit</li>
            </ul>
          </Section>

          <Section nr="5" title="Cookies uitschakelen of verwijderen">
            <p>
              Je kunt cookies beheren via de instellingen van jouw browser. Let op: het uitschakelen van
              functionele cookies kan er voor zorgen dat je niet meer kunt inloggen of de website niet
              correct functioneert.
            </p>
            <p>
              Instructies voor het beheren van cookies per browser:
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/nl/kb/cookies-verwijderen-gegevens-verwijderen-websites-s" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/nl-nl/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline">Apple Safari</a></li>
            </ul>
          </Section>

          <Section nr="6" title="Toestemming">
            <p>
              Door gebruik te maken van onze website ga je akkoord met het gebruik van functionele cookies die
              noodzakelijk zijn voor de werking van de site. Voor overige cookies vragen wij expliciet
              toestemming, voor zover van toepassing.
            </p>
          </Section>

          <Section nr="7" title="Wijzigingen in dit cookiebeleid">
            <p>
              Wij behouden het recht dit cookiebeleid te wijzigen. Wijzigingen worden gepubliceerd op deze pagina
              met vermelding van de datum van de meest recente aanpassing. Wij raden je aan dit beleid regelmatig
              te raadplegen.
            </p>
          </Section>

          <Section nr="8" title="Contact">
            <p>
              Heb je vragen over ons cookiebeleid? Neem dan contact op via:
            </p>
            <p className="text-[#C9A84C] font-semibold">info@fitsmith.nl</p>
            <p>FitSmith · KvK: (op aanvraag) · BTW: NL005444951B11</p>
          </Section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 text-center">
          <p className="text-zinc-600 text-xs">© 2026 FitSmith. Alle rechten voorbehouden.</p>
        </div>
      </div>
    </div>
  );
}
